// @ts-nocheck
// 🚀 V156.3: Network Data Context for persistent tab caching
// Prevents reloading when navigating between pages and returning to Network

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NetworkConnection as BaseNetworkConnection } from '../services/networkConnectionService';
import { useAuth } from '../hooks/useAuth';
import { supabase, withConnectionsAccess } from '../lib/supabaseGuard';
import { baseSupabase } from '../integrations/supabase/client'; // V334.S15: Bypass profiles RLS for show_age
import { toast } from 'sonner'; // V167.NOTIFY: Toast notifications for friend requests
import { clearProfileStatsCache } from '../utils/profileStatsCache'; // V192.4: Moved to separate file to break circular dependency
import { getBlockedUserIds } from '../services/friends/friendRequestService'; // V168.BLOCK: Filter blocked users
import { isConnectionVisible } from '../utils/visibilityUtils'; // V316: Dual-way visibility
import { trustScoreService } from '../services/TrustScoreService'; // V320: Single orchestrator for update-trust-score
import { safeAppReload } from '../utils/safeReload'; // V320.RELOAD: Safe reload utility
import type { NetworkDataContextType, NetworkConnection, NetworkTabCache, CachedUser, ConnectionRequest } from './network/NetworkDataContext.types';
import {
  APP_CONNECTION_SYNC,
  extractFirstDegreeUserIds
} from './network/connectionApprovalSync';
import { getPublicUserV1ById } from '../services/publicUsersV1Service';
import { extractGenderPreferenceList, mutualGenderDatingCompatible, normalizeUserGender } from '../utils/genderCompatibility';
import { resolveLookingForDate } from '../utils/datingVisibility';
import { isGabiDiscoverDebug, logGabiDiscoverStage, summarizeRejects } from '../utils/discoverGabiTrace';
import { useBlissLiveSync } from './blissLiveSync/BlissLiveSyncProvider';
import { useNetworkBlissLiveSyncEffects } from './network/useNetworkBlissLiveSyncEffects';
import { rpcIncomingConnectionRequestsForViewer } from '@/lib/d001PeerUsersRpc';
import { dedupeConnectionsForTabCache } from '@/utils/connectionPathSummary';
import {
  fetchOutgoingPendingRecipientIds,
  mergeOutgoingPendingIds,
  sameSortedIds,
  uniqueSortedRecipientIds,
} from '@/services/friends/outgoingPendingRequestReader';

export const EMPTY_OUTGOING_PENDING_IDS: ReadonlySet<string> = new Set();

/** PostgREST `.in()` with `undefined` becomes literal "undefined" → 22P02. Filter before any `in` on uuid columns. */
function isNonEmptyUuid(id: unknown): id is string {
  if (id == null || typeof id !== 'string') return false;
  const t = id.trim();
  if (t.length < 32 || t === 'undefined') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(t);
}

/** D001: Peer id from `connections` row only (no embedded `users`). Viewer is always one endpoint. */
function peerIdFromRelationshipRow(
  conn: { user_id?: string | null; connection_user_id?: string | null },
  currentUserId: string
): string | null {
  const a = conn.user_id;
  const b = conn.connection_user_id;
  if (!isNonEmptyUuid(a) || !isNonEmptyUuid(b)) return null;
  if (a === currentUserId) return b;
  if (b === currentUserId) return a;
  return null;
}

const NEW_BLISSNET_USER_LABEL = 'New BlissNet user';

function normalizeIncomingConnectionRequest(row: any): ConnectionRequest {
  const displayName =
    typeof row?.requester_display_name === 'string' && row.requester_display_name.trim()
      ? row.requester_display_name.trim()
      : NEW_BLISSNET_USER_LABEL;
  const avatarUrl =
    typeof row?.requester_avatar_url === 'string' && row.requester_avatar_url.trim()
      ? row.requester_avatar_url.trim()
      : null;

  return {
    id: row.request_id,
    sender_id: row.requester_id,
    recipient_id: row.recipient_id,
    invite_code: row.invite_code ?? null,
    status: row.status || 'pending',
    created_at: row.created_at,
    requester_display_name: displayName,
    requester_avatar_url: avatarUrl,
    sender: {
      id: row.requester_id,
      name: displayName,
      photo_urls: avatarUrl ? [avatarUrl] : null,
    },
  };
}

// V320.RELOAD.4: Inline NOOP to break circular import (noop file → types → services could pull context)
export const NOOP_CONTEXT: NetworkDataContextType = {
  tabCache: {
    all: [],
    first: [],
    second: [],
    third: [],
    favorites: [],
    lastUpdated: 0,
    userId: null,
  },
  updateTabCache: () => { },
  clearTabCache: () => { },
  isTabCacheValid: () => false,
  getTabConnections: () => [],
  secondDegreeConnections: [],
  secondDegreeCount: 0,
  thirdDegreeConnections: [],
  thirdDegreeCount: 0,
  refreshNetwork: async () => [],
  isRefreshing: false,
  isRefreshingConnectionRequests: false,
  connectionRequests: [],
  refreshConnectionRequests: async () => { },
  outgoingPendingRecipientIds: EMPTY_OUTGOING_PENDING_IDS,
  markOutgoingPending: () => { },
  refreshOutgoingPendingRequests: async () => { },
  removeConnectionRequest: () => { },
  getUser: () => null,
  getCurrentUser: () => null,
  updateCurrentUser: async () => { },
  updateCurrentUserProfile: async () => { },
  forceHardSync: async () => { },
  isSyncing: false,
  scheduleHardSync: () => { },
  retryBootstrap: async () => { },
  networkShellVisibleTab: null,
  setNetworkShellVisibleTab: () => { },
  hasUnseenNetworkAlerts: false,
  markNetworkAlertsSeen: () => { },
};

// V168.3RD: Trust score requirement for 3rd degree visibility (PRODUCTION VALUE)
export const THIRD_DEGREE_TRUST_REQUIREMENT = 120; // Production: 120 points = 12 friends minimum

// V164.2: Extended NetworkConnection with dating fields for safe Discover filtering
// V167.MERGE: Use date_of_birth instead of dob
interface EnrichedNetworkConnection extends BaseNetworkConnection {
  otherDateOfBirth?: string;
  otherGender?: string;
  otherDatingPreferences?: any;
  connectionRecordId?: string;
  connectionId?: string;
  connectionUserId?: string;
  connectionTargetUserId?: string;
}

export const NetworkDataContext = createContext<NetworkDataContextType | undefined>(undefined);

// V601: Bumped cache key so caches persisted under the previous degree semantics
// (where 2nd-degree rows could be stored without symmetric mutualFriends-based degree)
// are automatically dropped on next load instead of requiring a manual clear.
const CACHE_KEY = 'network_tab_cache_v2';
const LEGACY_CACHE_KEYS = ['network_tab_cache'] as const;
const DEFAULT_MAX_AGE = 300000; // 5 minutes
const LAST_SEEN_ALERTS_KEY_PREFIX = 'blissnet_network_alerts_last_seen_';

// V601: Remove pre-v2 cache entries once on module load so they cannot resurface via legacy keys.
try {
  for (const legacyKey of LEGACY_CACHE_KEYS) {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(legacyKey)) {
      localStorage.removeItem(legacyKey);
    }
  }
} catch {
  /* localStorage unavailable — safe to ignore */
}

export const NetworkDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('🚀 V171.UX: NetworkDataProvider rendering');
  const { user, isReady, getAuthGeneration, isAuthGenerationActive } = useAuth() as any;
  const navigate = useNavigate();
  console.log('🚀 V171.UX: Auth state - user:', user?.id, 'isReady:', isReady);

  const [tabCache, setTabCache] = useState<NetworkTabCache>(() => {
    // Try to restore from localStorage on init
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        const age = now - parsed.lastUpdated;

        // Validate cache timestamp
        if (parsed.lastUpdated > 0 && age >= 0 && age < 86400000) { // 24 hours max
          // V600: Merge tab buckets and dedupe — stale caches may list the same user in 2nd + 3rd
          const mergedRaw = [
            ...(Array.isArray(parsed.all) ? parsed.all : []),
            ...(Array.isArray(parsed.first) ? parsed.first : []),
            ...(Array.isArray(parsed.second) ? parsed.second : []),
            ...(Array.isArray(parsed.third) ? parsed.third : []),
          ];
          const deduped = dedupeConnectionsForTabCache(mergedRaw);
          const normalized: NetworkTabCache = {
            all: deduped,
            first: deduped.filter((c) => Number(c?.degree) === 1),
            second: deduped.filter((c) => Number(c?.degree) === 2),
            third: deduped.filter((c) => Number(c?.degree) === 3),
            favorites: Array.isArray(parsed.favorites)
              ? deduped.filter((c) => c?.userId && parsed.favorites.some((f: { userId?: string }) => f?.userId === c.userId))
              : [],
            lastUpdated: parsed.lastUpdated ?? 0,
            userId: parsed.userId ?? null
          };
          console.log('🔄 V156.3: Restored network tab cache from localStorage:', {
            all: normalized.all.length,
            third: normalized.third.length,
            age: Math.round(age / 1000) + 's'
          });
          return normalized;
        } else {
          console.log('🚨 V156.3: Corrupted cache detected, clearing:', {
            lastUpdated: parsed.lastUpdated,
            age: Math.round(age / 1000) + 's'
          });
          localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch (error) {
      console.warn('⚠️ V156.3: Failed to restore network tab cache:', error);
      localStorage.removeItem(CACHE_KEY);
    }

    return {
      all: [],
      first: [],
      second: [],
      third: [],
      favorites: [],
      lastUpdated: 0,
      userId: null
    };
  });

  // V163.2: Centralized user cache to reduce duplicate user queries (6-7 → 2-3)
  const [userCache, setUserCache] = useState<Map<string, CachedUser>>(new Map());
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  // V163.3: Consolidated connection requests
  const [connectionRequests, setConnectionRequests] = useState<ConnectionRequest[]>([]);
  const [outgoingPendingIds, setOutgoingPendingIds] = useState<string[]>([]);
  const outgoingPendingIdsRef = useRef<string[]>([]);
  const outgoingPendingFetchGenRef = useRef(0);
  // HOTFIX: Invalidate stale async connection_requests fetches (optimistic remove / overlapping refresh)
  const connectionRequestsFetchGenRef = useRef(0);
  const connectionRequestsInFlightRef = useRef(0);
  const [isRefreshingConnectionRequests, setIsRefreshingConnectionRequests] = useState(false);
  // P1 UX: Network page registers inner tab while `/network` is the active route (KeepAlive-safe).
  const [networkShellVisibleTab, setNetworkShellVisibleTab] = useState<string | null>(null);
  // P1 UX: lastSeenAlertsAt persisted per user; version bumps recompute hasUnseen
  const lastSeenAlertsAtRef = useRef<number>(0);
  const [lastSeenAlertsEpoch, setLastSeenAlertsEpoch] = useState(0);
  const prevUnseenAlertsRef = useRef(false);
  // V167.2ND: 2nd degree connections state
  const [secondDegreeConnections, setSecondDegreeConnections] = useState<NetworkConnection[]>([]);
  // V167.3RD: 3rd degree connections state (requires trust score >= 40)
  const [thirdDegreeConnections, setThirdDegreeConnections] = useState<NetworkConnection[]>([]);
  // V174.6: Network refresh state for mutations
  const [isRefreshing, setIsRefreshing] = useState(false);
  // V320.RELOAD.3: Epoch invalidate — forces loader to refetch (no manual refresh)
  const [networkEpoch, setNetworkEpoch] = useState(0);
  // Plan: suppress realtime during explicit refresh (explicit user action wins)
  const suppressRealtimeUntilRef = useRef<number>(0);
  // Plan: return first-degree connections from refreshNetwork for verification
  const lastFirstDegreeConnectionsRef = useRef<NetworkConnection[]>([]);
  // CURSOR PATCH: debounce realtime refresh bursts (shared for both sides)
  const rtDebounceRef = useRef<number | null>(null);

  // V320.SYNC: Hard sync state and timer for deterministic refresh
  const [isSyncing, setIsSyncing] = useState(false);
  const hardSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // CURSOR PATCH: Safe realtime scheduler for trust + UI refresh (both users)
  const scheduleRealtimeRefresh = (userIds: string[]) => {
    if (typeof window === 'undefined') return;

    if (rtDebounceRef.current !== null) {
      window.clearTimeout(rtDebounceRef.current);
    }

    rtDebounceRef.current = window.setTimeout(() => {
      (async () => {
        try {
          clearProfileStatsCache?.();

          // ✅ Recompute trust for BOTH users (server-side, no 403)
          const ids = Array.from(new Set(userIds.filter(Boolean)));
          if (ids.length) {
            await supabase.rpc('recompute_trust_for_users', { user_ids: ids });
          }

          // ✅ Refresh Network state (drives UI)
          await refreshNetwork?.();

          // ✅ Notify listeners (Profile, etc.) to recalc
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('connectionChanged'));
          }
        } catch (err) {
          console.warn('[NetworkData] realtime refresh failed', err);
        }
      })();
    }, 350);
  };

  // V167.INSTANT.FIX: Debug logging for connection requests state changes
  useEffect(() => {
    // Connection requests updated - no logging needed
  }, [connectionRequests]);

  // P1 UX: Load persisted last-seen timestamp when auth user changes
  useEffect(() => {
    if (!user?.id) {
      lastSeenAlertsAtRef.current = 0;
      prevUnseenAlertsRef.current = false;
      setLastSeenAlertsEpoch((e) => e + 1);
      return;
    }
    try {
      const raw = localStorage.getItem(`${LAST_SEEN_ALERTS_KEY_PREFIX}${user.id}`);
      lastSeenAlertsAtRef.current = raw ? parseInt(raw, 10) : 0;
    } catch {
      lastSeenAlertsAtRef.current = 0;
    }
    setLastSeenAlertsEpoch((e) => e + 1);
  }, [user?.id]);

  const markNetworkAlertsSeen = useCallback(() => {
    if (!user?.id) return;
    const ts =
      connectionRequests.length === 0
        ? Date.now()
        : Math.max(
            Date.now(),
            ...connectionRequests.map((r: ConnectionRequest) => new Date(r.created_at).getTime())
          );
    try {
      localStorage.setItem(`${LAST_SEEN_ALERTS_KEY_PREFIX}${user.id}`, String(ts));
    } catch {
      /* ignore quota */
    }
    lastSeenAlertsAtRef.current = ts;
    setLastSeenAlertsEpoch((e) => e + 1);
  }, [user?.id, connectionRequests]);

  const hasUnseenNetworkAlerts = useMemo(() => {
    const lastSeen = lastSeenAlertsAtRef.current;
    if (connectionRequests.length === 0) {
      if (isRefreshingConnectionRequests) {
        return prevUnseenAlertsRef.current;
      }
      return false;
    }
    const maxCreated = Math.max(
      ...connectionRequests.map((r: ConnectionRequest) => new Date(r.created_at).getTime())
    );
    return maxCreated > lastSeen;
  }, [connectionRequests, isRefreshingConnectionRequests, lastSeenAlertsEpoch]);

  useEffect(() => {
    if (!(isRefreshingConnectionRequests && connectionRequests.length === 0)) {
      prevUnseenAlertsRef.current = hasUnseenNetworkAlerts;
    }
  }, [hasUnseenNetworkAlerts, isRefreshingConnectionRequests, connectionRequests.length]);

  // V323: Listen for user injection from real-time match events (just-in-time profile for dialog/chat list)
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ userId: string; name: string; photo_urls?: string[] }>;
      const { userId, name, photo_urls } = ev.detail || {};
      if (!userId || !name) return;
      setUserCache(prev => {
        const next = new Map(prev);
        const existing = next.get(userId);
        next.set(userId, {
          id: userId,
          name,
          email: existing?.email ?? '',
          photo_urls: photo_urls ?? existing?.photo_urls ?? null,
          bio: existing?.bio ?? null,
          date_of_birth: existing?.date_of_birth ?? null,
          gender: existing?.gender ?? null,
          trust_score: existing?.trust_score ?? 0,
          is_verified: existing?.is_verified ?? false,
          friends_count: existing?.friends_count ?? 0,
          dating_preferences: existing?.dating_preferences ?? null,
          account_status: existing?.account_status ?? null,
          created_at: existing?.created_at ?? null,
          updated_at: existing?.updated_at ?? null,
          profiles: existing?.profiles
        });
        return next;
      });
    };
    window.addEventListener('blissnet-inject-user', handler);
    return () => window.removeEventListener('blissnet-inject-user', handler);
  }, []);

  // P0: Self profile photo recovery — merge fresh photo_urls into current user cache (no peer writes)
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ userId: string; photo_urls: string[] }>;
      const { userId, photo_urls } = ev.detail || {};
      if (!userId || !photo_urls?.length || !user?.id || userId !== user.id) return;
      setUserCache((prev) => {
        const next = new Map(prev);
        const existing = next.get(userId);
        if (!existing) return prev;
        next.set(userId, { ...existing, photo_urls });
        return next;
      });
    };
    window.addEventListener('blissnet-self-photo-urls-updated', handler);
    return () => window.removeEventListener('blissnet-self-photo-urls-updated', handler);
  }, [user?.id]);

  // V168.3RD: Load 3rd degree connections (friends of friends of friends)
  // ALWAYS load for Discover page, but hide in Network tabs if trust score < 40
  // Uses RPC-based approach that was working previously
  const load3rdDegreeConnections = useCallback(async (
    userId: string,
    secondDegreeConnections: NetworkConnection[],
    firstDegreeConnections: NetworkConnection[],
    userTrustScore: number,
    peerCache?: Map<string, CachedUser>,
  ): Promise<NetworkConnection[]> => {
    console.log('🎯 V168.3RD: Loading 3rd degree connections (trust score:', userTrustScore, ')');

    // V168.3RD: REMOVED trust score check - always load for Discover
    // Trust score check moved to Network page tabs only

    if (secondDegreeConnections.length === 0) {
      console.log('🎯 V168.3RD: No 2nd degree connections, skipping 3rd degree');
      return [];
    }

    try {
      // V167.3RD: Use RPC-based approach (was working previously)
      // Using RPC function for 3rd degree connections
      const { getCached3rdDegreeConnections } = await import('../services/rpcCacheService');

      let rpcThirdDegreeData;
      let rpcThirdError;

      try {
        rpcThirdDegreeData = await getCached3rdDegreeConnections(userId);
        // RPC returned results
      } catch (error) {
        rpcThirdError = error;
        console.error('❌ V167.3RD.RPC: Error getting 3rd degree connections via RPC:', error);
      }

      if (rpcThirdError || !rpcThirdDegreeData || rpcThirdDegreeData.length === 0) {
        console.log('🎯 V168.3RD: No 3rd degree data from RPC:', {
          hasError: !!rpcThirdError,
          dataLength: rpcThirdDegreeData?.length || 0
        });
        return [];
      }

      console.log('🎯 V168.3RD: RPC returned', rpcThirdDegreeData.length, '3rd degree connections');

      // Get IDs to exclude (1st and 2nd degree)
      const firstDegreeIds = firstDegreeConnections.map(c => c.userId);
      const secondDegreeIds = secondDegreeConnections.map(c => c.userId);
      const excludeIds = new Set([userId, ...firstDegreeIds, ...secondDegreeIds]);

      // Excluding 1st + 2nd degree + self

      // Convert RPC results to NetworkConnection format
      const thirdDegreeList: NetworkConnection[] = [];
      const processedUserIds = new Set<string>();

      for (const rpcResult of rpcThirdDegreeData) {
        const targetUserId = rpcResult.connection_user_id;

        // Processing 3rd degree user

        // Skip if already processed or should be excluded
        if (processedUserIds.has(targetUserId) || excludeIds.has(targetUserId)) {
          // Skipping duplicate/excluded user
          continue;
        }

        processedUserIds.add(targetUserId);

        // Find connection path: userId -> 1st -> 2nd -> 3rd
        // rpcResult.user_id is the 2nd degree user connecting to 3rd degree
        const connecting2ndDegreeId = rpcResult.user_id;
        const connecting2ndDegree = secondDegreeConnections.find(c => c.userId === connecting2ndDegreeId);

        if (!connecting2ndDegree) {
          // Skipping - 2nd degree connector not found
          continue;
        }

        // V168.1.6: Find ALL 1st degree connectors (mutual friends connecting to 2nd degree)
        // The 2nd degree connection has mutualFriends array with all 1st degree friends
        const all1stDegreeConnectors = connecting2ndDegree.mutualFriends || [];

        if (all1stDegreeConnectors.length === 0) {
          // Fallback to single connectedThrough
          const connecting1stDegreeId = connecting2ndDegree.connectedThrough;
          const connecting1stDegree = firstDegreeConnections.find(c => c.userId === connecting1stDegreeId);

          if (!connecting1stDegree) {
            // Skipping - 1st degree connector not found
            continue;
          }

          all1stDegreeConnectors.push({
            id: connecting1stDegree.userId,
            name: connecting1stDegree.name,
            trustScore: connecting1stDegree.trustScore || 0
          });
        }

        // V168.1.6: Build connection path with ALL 1st degree connectors
        // Format: You -> [All 1st degree friends] -> 2nd degree -> 3rd degree
        const connectionPath = [
          { id: userId, name: 'You', degree: 0 },
          // Add all 1st degree connectors
          ...all1stDegreeConnectors.map((connector: any) => ({
            id: connector.id,
            name: connector.name,
            degree: 1,
            trustScore: connector.trustScore
          })),
          { id: connecting2ndDegree.userId, name: connecting2ndDegree.name, degree: 2 },
          { id: targetUserId, name: rpcResult.connection_name || 'Unknown', degree: 3 }
        ];

        const thirdDegreeConn: NetworkConnection = {
          id: targetUserId,
          userId: targetUserId,
          name: rpcResult.connection_name || 'Unknown',
          bio: rpcResult.connection_bio || '',
          avatar: rpcResult.connection_photo_urls?.[0] || null,
          connectionType: 'FRIEND' as const,
          status: 'ACCEPTED' as const,
          degree: 3,
          mutualConnections: 0,
          isFavorited: false,
          connectionPath: connectionPath,
          connectedThrough: connecting2ndDegreeId,
          connectedThroughName: connecting2ndDegree.name,
          trustScore: rpcResult.connection_trust_score || 0,
          isVerified: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as NetworkConnection & { connectionPath?: Array<{ id: string; name: string; degree: number }> };

        thirdDegreeList.push(thirdDegreeConn);
        // Added 3rd degree connection
      }

      console.log('✅ V168.3RD: Loaded', thirdDegreeList.length, '3rd degree connections');

      // BlissNet: hydrate 3rd-degree peers with dating_preferences for Discover filtering
      const thirdPeerIds = thirdDegreeList.map(c => c.userId).filter(isNonEmptyUuid);
      if (thirdPeerIds.length > 0) {
        const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
        const { data: thirdPeerRows, error: thirdPeerErr } = await rpcPeerUsersForViewer(supabase, thirdPeerIds, {
          includePrefs: true
        });
        if (thirdPeerErr) {
          console.warn('⚠️ BlissNet: 3rd-degree peer hydration failed:', thirdPeerErr);
        } else {
          const thirdPeerMap = new Map<string, any>();
          (thirdPeerRows || []).forEach((row: any) => {
            if (row?.id) {
              thirdPeerMap.set(row.id, row);
              if (peerCache) peerCache.set(row.id, row as CachedUser);
            }
          });
          return thirdDegreeList.map(conn => {
            const peer = thirdPeerMap.get(conn.userId);
            const isVerified = peer?.is_verified === true || conn.isVerified === true;
            if (!peer) return { ...conn, isVerified };
            return {
              ...conn,
              otherDateOfBirth: peer.date_of_birth,
              otherGender: peer.gender,
              otherDatingPreferences: peer.dating_preferences,
              otherFriendsCount: peer.friends_count,
              otherSecondDegreeCount: peer.second_degree_count,
              trustScore: Number(peer.trust_score) || conn.trustScore || 0,
              isVerified,
            };
          });
        }
      }

      return thirdDegreeList;

    } catch (error) {
      console.error('❌ V167.3RD.RPC: Exception loading 3rd degree:', error);
      return [];
    }
  }, []);

  // V167.2ND: Load 2nd degree connections (friends of friends)
  const load2ndDegreeConnections = useCallback(async (
    userId: string,
    directFriendIds: string[],
    directFriends: NetworkConnection[] = []
  ): Promise<NetworkConnection[]> => {
    // Loading 2nd degree connections

    const friendIdsForIn = directFriendIds.filter(isNonEmptyUuid);
    if (friendIdsForIn.length === 0) {
      return [];
    }

    try {
      // D001: 2nd degree — relationship rows only; hydrate FoF via peer_users_for_viewer
      const { data: friendsAsSource, error: error1 } = await withConnectionsAccess(() =>
        supabase
          .from('connections')
          .select(`
            user_id,
            connection_user_id,
            type,
            status
          `)
          .in('user_id', friendIdsForIn)
          .eq('type', 'FRIEND')
          .eq('status', 'ACCEPTED')
          .neq('connection_user_id', userId)
      );

      if (error1) {
        console.error('❌ V167.2ND.QUERY1: Error:', error1);
      }

      const { data: friendsAsTarget, error: error2 } = await withConnectionsAccess(() =>
        supabase
          .from('connections')
          .select(`
            user_id,
            connection_user_id,
            type,
            status
          `)
          .in('connection_user_id', friendIdsForIn)
          .eq('type', 'FRIEND')
          .eq('status', 'ACCEPTED')
          .neq('user_id', userId)
      );

      if (error2) {
        console.error('❌ V167.2ND.QUERY2: Error:', error2);
      }

      const friendsOfFriends = [
        ...((friendsAsSource || []) as any[]).map((r) => ({ ...r, _fofSide: 'source' as const })),
        ...((friendsAsTarget || []) as any[]).map((r) => ({ ...r, _fofSide: 'target' as const }))
      ];

      const uniqueSecondDegree = new Map<string, {
        connection: any;
        mutualFriends: Array<{ id: string; name: string; trustScore: number }>;
      }>();

      friendsOfFriends.forEach((connection: any) => {
        const otherUserId =
          connection._fofSide === 'source' ? connection.connection_user_id : connection.user_id;

        if (!otherUserId) {
          return;
        }
        if (!isNonEmptyUuid(otherUserId)) {
          return;
        }

        if (directFriendIds.includes(otherUserId)) {
          return;
        }

        const mutualFriendId =
          connection._fofSide === 'source' ? connection.user_id : connection.connection_user_id;
        if (!isNonEmptyUuid(mutualFriendId)) {
          return;
        }
        const mutualFriend = directFriends.find(f => f.userId === mutualFriendId);

        if (!mutualFriend) {
          return;
        }

        if (!uniqueSecondDegree.has(otherUserId)) {
          uniqueSecondDegree.set(otherUserId, {
            connection: connection,
            mutualFriends: []
          });
        }

        const entry = uniqueSecondDegree.get(otherUserId)!;
        if (!entry.mutualFriends.find(mf => mf.id === mutualFriendId)) {
          const trustScore = mutualFriend.trustScore ?? 0;
          entry.mutualFriends.push({
            id: mutualFriendId,
            name: mutualFriend.name,
            trustScore: trustScore
          });
        }
      });

      const fofIds = Array.from(uniqueSecondDegree.keys()).filter(isNonEmptyUuid);
      const secondDegreePeerMap = new Map<string, any>();
      if (fofIds.length > 0) {
        const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
        const { data: fofRows, error: fofRpcErr } = await rpcPeerUsersForViewer(supabase, fofIds, {
          includePrefs: true
        });
        if (fofRpcErr) {
          console.error('❌ D001: peer_users_for_viewer (2nd degree) failed:', fofRpcErr);
        }
        (fofRows || []).forEach((row: any) => {
          if (row?.id) secondDegreePeerMap.set(row.id, row);
        });
      }

      const secondDegreeList: NetworkConnection[] = Array.from(uniqueSecondDegree.entries()).map(([friendOfFriendId, data]) => {
        const conn = data.connection;
        const fofPeer = secondDegreePeerMap.get(friendOfFriendId);

        const sortedMutualFriends = [...data.mutualFriends].sort((a, b) => {
          const scoreA = Number(a.trustScore) || 0;
          const scoreB = Number(b.trustScore) || 0;
          return scoreB - scoreA;
        });

        const primaryMutualFriend = sortedMutualFriends[0];
        const urls = fofPeer?.photo_urls;
        const avatarUrl =
          Array.isArray(urls) && urls.length > 0 && typeof urls[0] === 'string' ? urls[0] : null;

        return {
          id: friendOfFriendId,
          userId: friendOfFriendId,
          name: fofPeer?.name || 'Unknown',
          bio: fofPeer?.bio || '',
          avatar: avatarUrl,
          connectionType: 'FRIEND' as const,
          status: 'ACCEPTED' as const,
          degree: 2,
          mutualConnections: sortedMutualFriends.length,
          isFavorited: false,
          connectedThrough: primaryMutualFriend?.id,
          connectedThroughName: primaryMutualFriend?.name || 'Mutual Friend',
          mutualFriends: sortedMutualFriends.map(mf => ({
            id: mf.id,
            name: mf.name,
            trustScore: mf.trustScore
          })),
          otherDateOfBirth: fofPeer?.date_of_birth,
          otherGender: fofPeer?.gender,
          otherDatingPreferences: fofPeer?.dating_preferences,
          otherFriendsCount: fofPeer?.friends_count,
          otherSecondDegreeCount: fofPeer?.second_degree_count,
          trustScore: Number(fofPeer?.trust_score) || 0,
          isVerified: fofPeer?.is_verified === true,
        } as NetworkConnection & {
          mutualFriends?: Array<{ id: string; name: string; trustScore: number }>;
          otherFriendsCount?: number;
          otherSecondDegreeCount?: number;
        };
      });

      return secondDegreeList;

    } catch (error) {
      console.error('❌ V167.2ND: Exception loading 2nd degree:', error);
      return [];
    }
  }, []);

  // V174.8: Track initialization state (must be before loadAllNetworkData)
  const hasInitialized = useRef(false);
  // V320: Debounce stability gate — wait 1200ms quiet period then commitStable once
  const trustStableTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTrustCountsRef = useRef<{ userId: string; friends: number; second: number } | null>(null);

  // V174.8: Moved updateTabCache ABOVE loadAllNetworkData to fix TDZ crash
  const updateTabCache = useCallback((connections: NetworkConnection[], favorites: Set<string>, userId: string) => {
    const now = Date.now();
    console.log('🔄 V172.FIX3: updateTabCache called with', connections.length, 'connections');

    // V160.3: Memoize filtered arrays to prevent unnecessary re-renders
    // V323.BUG7: Use Number(c.degree) for robustness (handles string "3" from JSON/RPC)
    setTabCache(prevCache => {
      const conns = dedupeConnectionsForTabCache(Array.isArray(connections) ? connections : []);
      const favSet = favorites instanceof Set ? favorites : new Set<string>();
      const newCache: NetworkTabCache = {
        all: conns,
        first: conns.filter(c => Number(c?.degree) === 1),
        second: conns.filter(c => Number(c?.degree) === 2),
        third: conns.filter(c => Number(c?.degree) === 3),
        favorites: conns.filter(c => c?.userId && favSet.has(c.userId)),
        lastUpdated: now,
        userId
      };

      console.log('✅ V172.FIX3: Cache updated - all:', newCache.all.length, 'first:', newCache.first.length, 'second:', newCache.second.length, 'third:', newCache.third.length);
      return newCache;
    });
  }, []);

  // V174.8: Clear only persisted cache, keep UI state stable
  // MOVED ABOVE loadAllNetworkData to fix TDZ crash (dependency array references this)
  const clearPersistedTabCacheOnly = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.warn('⚠️ V174.8: Failed to clear persisted network tab cache:', error);
    }
    // IMPORTANT: do NOT touch state arrays here
  }, []);

  // V174.8: Extract loadAllNetworkData as useCallback so refreshNetwork can call it
  // V320.RELOAD.3: opts.force bypasses cache and in-flight guard for deterministic refresh
  const loadAllNetworkData = useCallback(async (opts?: { force?: boolean }) => {
    if (!user?.id) {
      console.warn('⚠️ V174.8: Cannot load network data - no user ID');
      return;
    }
    if (opts?.force) {
      clearPersistedTabCacheOnly();
      hasInitialized.current = false;
    }
    hasInitialized.current = true;
    // SMART MERGING - Loading user connections + general data efficiently
    const startTime = Date.now();
    try {
      // V174.7: Load blocked user IDs first to filter them out (BIDIRECTIONAL)
      const blockedUserIds = await getBlockedUserIds(user.id);
      const blockedSet = new Set(blockedUserIds);
      console.log('🔒 V174.7: Loaded', blockedUserIds.length, 'blocked users (bidirectional):', blockedUserIds);

      // V316: Fetch viewer's discovery_visibility for dual-way filtering
      let viewerVisibility = '1st_2nd_3rd';
      try {
        const { data: viewerProfile } = await supabase
          .from('profiles')
          .select('discovery_visibility')
          .eq('user_id', user.id)
          .maybeSingle();
        viewerVisibility = viewerProfile?.discovery_visibility || '1st_2nd_3rd';
      } catch {
        // Default to full visibility on error
      }

      // V163.8.5: STRATEGY 1 - Load USER-SPECIFIC connections first (most important)
      // Step 1a - Load ACTIVE matches for current user so we can mark connections as mutual matches
      let activeMatchUserIds = new Set<string>();
      try {
        const { data: matchRows, error: matchesError } = await supabase
          .from('matches')
          .select('user1_id, user2_id, status')
          .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
          .eq('status', 'active');

        if (matchesError) {
          console.error('❌ V167.NETWORK: Error fetching active matches for Network view:', matchesError);
        } else if (Array.isArray(matchRows) && matchRows.length > 0) {
          const ids = new Set<string>();
          matchRows.forEach((match: any) => {
            const otherId = match.user1_id === user.id ? match.user2_id : match.user1_id;
            if (otherId) {
              ids.add(otherId);
            }
          });
          activeMatchUserIds = ids;
          console.log('💖 V167.NETWORK: Active matches loaded for Network view:', Array.from(activeMatchUserIds));
        }
      } catch (matchErr) {
        console.error('❌ V167.NETWORK: Exception while loading active matches for Network view:', matchErr);
      }

      // Step 1b - D001: relationship columns only; hydrate peers via peer_users_for_viewer (no embedded users)
      const { data: userConnections, error: userConnError } = await withConnectionsAccess(() =>
        supabase
          .from('connections')
          .select(`
            id,
            user_id,
            connection_user_id,
            status,
            type,
            created_at,
            updated_at
          `)
          .or(`user_id.eq.${user.id},connection_user_id.eq.${user.id}`)
          .eq('status', 'ACCEPTED')
          .order('created_at', { ascending: false })
      );

      if (userConnError) throw userConnError;

      const connections = userConnections;
      const rawRows = connections || [];
      const peerIds1st = [
        ...new Set(rawRows.map((conn: any) => peerIdFromRelationshipRow(conn, user.id)).filter(isNonEmptyUuid))
      ];

      const firstDegreePeerMap = new Map<string, any>();
      if (peerIds1st.length > 0) {
        const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
        const { data: peerRows, error: rpcHydrateErr } = await rpcPeerUsersForViewer(supabase, peerIds1st, {
          includePrefs: true
        });
        if (rpcHydrateErr) {
          console.error('❌ D001: peer_users_for_viewer (1st degree) failed:', rpcHydrateErr);
        }
        (peerRows || []).forEach((row: any) => {
          if (row?.id) firstDegreePeerMap.set(row.id, row);
        });
      }

      // Map to NetworkConnection[] expected by consumers
      // V168.BLOCK: Filter out blocked users during mapping
      const mappedConnections: NetworkConnection[] = rawRows
        .map((conn: any, index: number) => {
          const peerId = peerIdFromRelationshipRow(conn, user.id);
          if (!peerId) {
            console.warn('⚠️ V174.8: Skipping connection — malformed relationship row:', conn?.id);
            return null;
          }

          const otherUser = firstDegreePeerMap.get(peerId);
          if (!otherUser) {
            console.warn('⚠️ D001: Peer not in viewer RPC result:', peerId, 'connection', conn?.id);
            return null;
          }

          // V174.7: Skip blocked users (bidirectional - mutual invisibility)
          if (blockedSet.has(peerId)) {
            console.log('🔒 V174.7: Filtering blocked user from 1st degree:', otherUser?.name || peerId);
            return null; // Will be filtered out
          }

          // V168.DISCONNECT.DEBUG: Log connection mapping to verify connectionId is set
          if (!conn.id) {
            console.warn('⚠️ DISCONNECT: Connection missing ID:', {
              user_id: conn.user_id,
              connection_user_id: conn.connection_user_id,
              otherUserId: peerId,
              name: otherUser?.name,
              fullConn: conn
            });
          } else if (index < 3) {
            // V168.DISCONNECT.DEBUG: Log successful mapping (first 3 connections only to avoid spam)
            console.log('✅ DISCONNECT: Mapped connection with ID:', {
              connectionTableId: conn.id,
              userId: peerId,
              name: otherUser?.name,
              status: conn.status
            });
          }

          const isMatch = activeMatchUserIds.has(peerId);

          const urls = otherUser.photo_urls;
          const primaryPhoto =
            Array.isArray(urls) && urls.length > 0 && typeof urls[0] === 'string' ? urls[0] : null;

          return {
            id: peerId,
            userId: peerId,
            name: otherUser.name,
            bio: otherUser.bio || '',
            avatar: primaryPhoto,
            degree: 1 as const,
            status: conn.status,
            mutualConnections: 0,
            isFavorited: false, // 🎯 V171.FAVORITES: Cache-only - favorites loaded from localStorage in Network.tsx
            // Match metadata for 1st degree connections
            isMatch,
            matchStatus: isMatch ? 'active' : null,
            connectionType: conn.type || 'friend',
            createdAt: conn.created_at,
            updatedAt: conn.updated_at,
            trustScore: Number(otherUser.trust_score) || 0, // Ensure numeric conversion
            isVerified: otherUser.is_verified === true,
            // Optional fields used in some views
            connectedThrough: undefined,
            connectedThroughName: undefined,
            // V164.2: Enrich with dating data for safe Discover filtering
            // V167.MERGE: Use date_of_birth instead of dob
            otherDateOfBirth: otherUser.date_of_birth,
            otherGender: otherUser.gender,
            otherDatingPreferences: otherUser.dating_preferences,
            // V168.DISCONNECT: Store connection record ID for direct database queries
            // CRITICAL: conn.id is the connection table primary key (e.g., '751d44e8-7285-46be-ba26-1489e3b5e6e8')
            connectionRecordId: conn.id,
            connectionId: conn.id, // Alias for simpler disconnect function
            connectionUserId: conn.user_id,
            connectionTargetUserId: conn.connection_user_id
          } as NetworkConnection;
        })
        .filter((conn): conn is NetworkConnection => conn !== null); // V168.BLOCK: Remove blocked users

      // 🎯 V171.FAVORITES: Load favorites from localStorage (cache-only, no DB)
      // Network.tsx will load and apply favorites from localStorage after connections load
      const favorites = new Set<string>();

      // V167.2ND: Load 2nd degree connections after direct connections are loaded
      // Mapped connections processed

      // V167.2ND.FIX: Use case-insensitive comparison for connection type
      const directFriends = mappedConnections.filter(c =>
        c.connectionType?.toUpperCase() === 'FRIEND' && c.status === 'ACCEPTED'
      );
      const directFriendIds = directFriends.map(f => f.userId).filter(isNonEmptyUuid);

      // Loading 2nd degree connections

      // Load 2nd degree connections (pass directFriends for mutual friend name lookup)
      const secondDegree = await load2ndDegreeConnections(user.id, directFriendIds, directFriends);
      // V174.7: Filter out blocked users from 2nd degree connections (bidirectional)
      const filteredSecondDegree = secondDegree
        .filter(conn => {
          const isBlocked = blockedSet.has(conn.userId);
          if (isBlocked) {
            console.log('🔒 V174.7: Filtering blocked user from 2nd degree:', conn.name || conn.userId);
          }
          return !isBlocked;
        })
        .map(conn => {
          const isMatch = activeMatchUserIds.has(conn.userId);
          return {
            ...conn,
            isMatch,
            matchStatus: isMatch ? 'active' : conn.matchStatus || null,
          } as NetworkConnection;
        });

      // V316: Dual-way visibility filter — fetch other users' discovery_visibility and filter connections
      const connectionUserIds = [
        ...mappedConnections.map(c => c.userId),
        ...filteredSecondDegree.map(c => c.userId)
      ].filter(isNonEmptyUuid);
      let visibilityMap = new Map<string, string>();
      if (connectionUserIds.length > 0) {
        try {
          const { data: profilesData } = await supabase.rpc('get_discovery_visibility_batch', {
            user_ids: connectionUserIds
          });
          if (profilesData) {
            (profilesData as { user_id: string; discovery_visibility: string }[]).forEach(p =>
              visibilityMap.set(p.user_id, p.discovery_visibility || '1st_2nd_3rd')
            );
          }
        } catch {
          // Default to visible on error
        }
      }
      const filterByVisibility = (conns: NetworkConnection[], degree: number) =>
        conns.filter(c => isConnectionVisible(degree, viewerVisibility, visibilityMap.get(c.userId)));
      const visibleFirst = filterByVisibility(mappedConnections, 1);
      const visibleSecond = filterByVisibility(filteredSecondDegree, 2);

      setSecondDegreeConnections(visibleSecond);

      // Data loading complete

      // V167.2ND.FIX: Update tab cache with 1st and 2nd degree connections first
      // V316: Use visibility-filtered connections
      const allConnectionsInitial = [...visibleFirst, ...visibleSecond];
      updateTabCache(allConnectionsInitial, favorites, user.id);
      // Plan: store first-degree for refreshNetwork return (verification in panel)
      lastFirstDegreeConnectionsRef.current = visibleFirst;

      // ------------------------ V163.2: Build user cache ------------------------
      setCurrentUserId(user.id);

      const newCache = new Map<string, CachedUser>();

      // D001: Peers from peer_users_for_viewer only (connection rows have no embedded users)
      firstDegreePeerMap.forEach((peerRow, peerId) => {
        if (!newCache.has(peerId)) {
          newCache.set(peerId, peerRow as CachedUser);
        }
      });

      // V163.8.5: SMART USER LOADING - Load current user + profile in single efficient batch
      // Step 2 - Loading CURRENT USER data
      try {
        // V167.MERGE.FIX3: Use maybeSingle() to handle new users without records
        // V177.VERIFY: Added friends_count to current user query for Trust Score calculation
        const { data: currentUserData, error: userError } = await supabase
          .from('users')
          .select(`
              id, name, email, photo_urls, bio, date_of_birth, gender, trust_score,
              is_verified, friends_count, second_degree_count, dating_preferences, account_status,
              created_at, updated_at
            `)
          .eq('id', user.id)
          .maybeSingle();

        if (userError || !currentUserData) {
          // V206.17: Suppress warning for new users (expected during onboarding)
          if (import.meta.env.DEV) {
            console.log('ℹ️ V206.17: No user data yet (new user in onboarding)');
          }
        } else {
          // Load profile data and merge (this is the ONLY profile query needed)
          // V334: Added show_age for per-user age visibility preference
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select(`
                id,
                relationship_status, dating_goal,
                discovery_visibility, show_age
              `)
            .eq('user_id', user.id)
            .maybeSingle();

          // Create complete user object that satisfies ALL app requirements
          const completeUserData = {
            ...currentUserData,
            profiles: profileData || null
          };

          newCache.set(user.id, completeUserData as CachedUser);
          // Loaded COMPLETE current user
        }
      } catch (error) {
        console.warn('⚠️ V163.8.5: Error loading current user:', error);
      }

      // Always update cache even if user data failed to load
      setUserCache(newCache);
      // Cached users

      // V320: Single orchestrator — debounce 1200ms quiet period, then commitStable once (no immediate write)
      // V316: Use visibility-filtered counts for trust score
      const friendsCount = visibleFirst.filter(c =>
        c.connectionType?.toUpperCase() === 'FRIEND' && c.status === 'ACCEPTED'
      ).length;
      const secondDegreeCount = visibleSecond.length;
      pendingTrustCountsRef.current = { userId: user.id, friends: friendsCount, second: secondDegreeCount };
      if (trustStableTimerRef.current) clearTimeout(trustStableTimerRef.current);
      trustStableTimerRef.current = setTimeout(() => {
        trustStableTimerRef.current = null;
        const p = pendingTrustCountsRef.current;
        if (p) {
          // Do not send is_verified — billing/subscription paths own verification state.
          trustScoreService.commitStable(p.userId, { friends: p.friends, second: p.second }, Date.now(), 'network_context_stable').catch(() => { });
        }
      }, 1200);

      // V168.3RD: ALWAYS load 3rd degree connections for Discover page
      // Trust score check moved to Network page tabs only
      let thirdDegree: NetworkConnection[] = [];
      const currentUserData = newCache.get(user.id);
      const userTrustScore = currentUserData?.trust_score || 0;
      console.log('🎯 V168.3RD: User trust score from DB:', userTrustScore, '(40+ needed for Network tabs)');
      console.log('🎯 V168.3RD: Current user data:', currentUserData);

      // V168.3RD: ALWAYS load 3rd degree (removed trust score check)
      if (filteredSecondDegree.length > 0) {
        thirdDegree = await load3rdDegreeConnections(user.id, filteredSecondDegree, mappedConnections, userTrustScore, newCache);
        // V174.7: Filter out blocked users from 3rd degree connections (bidirectional)
        const filteredThirdDegree = thirdDegree.filter(conn => {
          const isBlocked = blockedSet.has(conn.userId);
          if (isBlocked) {
            console.log('🔒 V174.7: Filtering blocked user from 3rd degree:', conn.name || conn.userId);
          }
          return !isBlocked;
        });
        // V334: Fetch 3rd degree users' discovery_visibility via RPC (bypasses RLS)
        const thirdDegreeIds = filteredThirdDegree.map(c => c.userId).filter(Boolean);
        if (thirdDegreeIds.length > 0) {
          try {
            const { data: thirdProfiles } = await supabase.rpc('get_discovery_visibility_batch', {
              user_ids: thirdDegreeIds
            });
            if (thirdProfiles) {
              (thirdProfiles as { user_id: string; discovery_visibility: string }[]).forEach(p =>
                visibilityMap.set(p.user_id, p.discovery_visibility || '1st_2nd_3rd')
              );
            }
          } catch {
            // Default to visible on error
          }
        }
        const visibleThird = filterByVisibility(filteredThirdDegree, 3);
        setThirdDegreeConnections(visibleThird);
        // 3rd degree connections loaded

        // Update tab cache with 3rd degree connections — V316: Use visibility-filtered
        const allConnectionsWithThird = [...visibleFirst, ...visibleSecond, ...visibleThird];
        console.log('🎯 V168.3RD: Updating tab cache with:', {
          firstDegree: visibleFirst.length,
          secondDegree: visibleSecond.length,
          thirdDegree: visibleThird.length,
          total: allConnectionsWithThird.length
        });
        updateTabCache(allConnectionsWithThird, favorites, user.id);
        setUserCache(new Map(newCache));
      } else {
        console.log('🎯 V168.3RD: Skipping 3rd degree - no 2nd degree connections');
      }
      // -------------------------------------------------------------------------

      // =========================== V163.8.5: SMART REQUESTS ============================
      // Step 3 - Loading USER connection requests (stale completion guard + in-flight for badge)
      const crLoadGen = ++connectionRequestsFetchGenRef.current;
      connectionRequestsInFlightRef.current += 1;
      if (connectionRequestsInFlightRef.current === 1) {
        setIsRefreshingConnectionRequests(true);
      }
      try {
        const { data: incomingRequests, error: requestsError } = await rpcIncomingConnectionRequestsForViewer(supabase);

        if (!requestsError) {
          if (crLoadGen !== connectionRequestsFetchGenRef.current) {
            console.log('🔕 HOTFIX: Skipped stale connection_requests apply from loadAllNetworkData');
          } else {
            const userRequests = (incomingRequests || []).map(normalizeIncomingConnectionRequest);
            setConnectionRequests(userRequests);
            // Add senders into cache for instant access
            userRequests.forEach((req: any) => {
              if (req.sender?.id && !newCache.has(req.sender.id)) {
                newCache.set(req.sender.id, req.sender as CachedUser);
              }
            });
          }
        } else {
          console.error('❌ V163.8.5: Error loading user connection requests:', requestsError);
        }
      } catch (e) {
        console.warn('⚠️ V163.8.5: Skipped user requests load due to error:', e);
      } finally {
        connectionRequestsInFlightRef.current -= 1;
        if (connectionRequestsInFlightRef.current === 0) {
          setIsRefreshingConnectionRequests(false);
        }
      }

      try {
        const outgoingGen = ++outgoingPendingFetchGenRef.current;
        const snapshot = new Set(outgoingPendingIdsRef.current);
        const { data: outgoingIds, error: outgoingError } = await fetchOutgoingPendingRecipientIds(user.id);
        if (outgoingError) {
          console.error('❌ Outgoing pending requests load failed; retaining last known state:', outgoingError);
        } else if (outgoingGen === outgoingPendingFetchGenRef.current) {
          const markedDuring = outgoingPendingIdsRef.current.filter((id) => !snapshot.has(id));
          const next = mergeOutgoingPendingIds({
            fetched: outgoingIds || [],
            markedDuringFetch: markedDuring,
            firstDegreeUserIds: extractFirstDegreeUserIds(visibleFirst),
          });
          if (!sameSortedIds(outgoingPendingIdsRef.current, next)) {
            outgoingPendingIdsRef.current = next;
            setOutgoingPendingIds(next);
          }
        }
      } catch (e) {
        console.warn('⚠️ Outgoing pending requests load exception; retaining last known state:', e);
      }
      // =========================================================================

      const duration = Date.now() - startTime;
      // SMART MERGING complete
    } catch (error) {
      console.error('❌ V162.1: Failed to load network data', error);
      // On failure, allow re-attempt on next mount/navigation
      hasInitialized.current = false;
    }
  }, [
    user?.id,
    load2ndDegreeConnections,
    load3rdDegreeConnections,
    updateTabCache,
    clearPersistedTabCacheOnly,
  ]); // V174.8: Fixed dependencies to prevent stale closures

  // V174.8: Separate useEffect to trigger initial load
  // V320.RELOAD.3: networkEpoch in deps — when epoch bumps, force refetch (no manual refresh)
  useEffect(() => {
    if (!isReady || !user?.id) return;
    const force = networkEpoch > 0;
    if (!force && hasInitialized.current) return;
    hasInitialized.current = true;
    void loadAllNetworkData({ force });
  }, [user?.id, isReady, networkEpoch, loadAllNetworkData]);

  // Persist to localStorage whenever cache changes
  useEffect(() => {
    if (tabCache.lastUpdated > 0) {
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(tabCache));
        // V159.2.5: Silenced persist spam
        // console.log('💾 V156.3: Persisted network tab cache to localStorage');
      } catch (error) {
        console.warn('⚠️ V156.3: Failed to persist network tab cache:', error);
      }
    }
  }, [tabCache]);

  const clearTabCache = useCallback(() => {
    console.log('🧹 V156.3: Clearing network tab cache');
    setTabCache({
      all: [],
      first: [],
      second: [],
      third: [],
      favorites: [],
      lastUpdated: 0,
      userId: null
    });

    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.warn('⚠️ V156.3: Failed to clear network tab cache from localStorage:', error);
    }

    // V170.9: Reset initialization flag so connections can reload after cache clear
    hasInitialized.current = false;
    console.log('🔄 V170.9: Reset hasInitialized flag to allow connection reload');
  }, []);

  const isTabCacheValid = useCallback((maxAgeMs = DEFAULT_MAX_AGE) => {
    if (tabCache.lastUpdated === 0 || tabCache.all.length === 0) {
      console.log('🔍 V156.3: Tab cache invalid - no data or timestamp');
      return false;
    }

    const now = Date.now();
    const age = now - tabCache.lastUpdated;

    // Sanity check: if age is negative or unreasonably large, cache is corrupted
    if (age < 0 || age > 86400000) { // 24 hours max
      console.log('🚨 V156.3: Tab cache corrupted - invalid timestamp:', {
        now,
        lastUpdated: tabCache.lastUpdated,
        age: Math.round(age / 1000) + 's'
      });
      return false;
    }

    const isValid = age < maxAgeMs;

    // V159.2.5: Silenced validity check spam
    // console.log('🔍 V156.3: Tab cache validity check:', {
    //   age: Math.round(age / 1000) + 's',
    //   maxAge: Math.round(maxAgeMs / 1000) + 's',
    //   valid: isValid,
    //   connections: tabCache.all.length
    // });

    return isValid;
  }, [tabCache]);

  // V323.BUG7: Safe accessors - prevent NaN/undefined when arrays missing
  const getTabConnections = useCallback((tab: string): NetworkConnection[] => {
    const all = Array.isArray(tabCache.all) ? tabCache.all : [];
    const first = Array.isArray(tabCache.first) ? tabCache.first : [];
    const second = Array.isArray(tabCache.second) ? tabCache.second : [];
    const third = Array.isArray(tabCache.third) ? tabCache.third : [];
    const favorites = Array.isArray(tabCache.favorites) ? tabCache.favorites : [];
    switch (tab) {
      case 'all': return all;
      case 'first': return first;
      case 'second': return second;
      case 'third': return third;
      case 'favorites': return favorites;
      default: return all;
    }
  }, [tabCache]);

  // V163.2: User cache accessors
  const getUser = useCallback((userId: string): CachedUser | null => {
    return userCache.get(userId) || null;
  }, [userCache]);

  const getCurrentUser = useCallback((): CachedUser | null => {
    if (!currentUserId) return null;
    return userCache.get(currentUserId) || null;
  }, [userCache, currentUserId]);

  // V163.8.5: Refresh USER connection requests — latest completion wins; in-flight for Network alerts tab guard
  const refreshConnectionRequests = useCallback(async () => {
    if (!user?.id) return;
    const authGen = typeof getAuthGeneration === 'function' ? getAuthGeneration() : 0;
    const myGen = ++connectionRequestsFetchGenRef.current;
    connectionRequestsInFlightRef.current += 1;
    if (connectionRequestsInFlightRef.current === 1) {
      setIsRefreshingConnectionRequests(true);
    }
    try {
      console.log('🔄 V167.INSTANT.FIX: Refreshing USER connection requests (recipient-specific)');

      const { data: incomingRequests, error } = await rpcIncomingConnectionRequestsForViewer(supabase);

      if (error) {
        console.error('❌ V167.INSTANT.FIX: Error fetching requests:', error);
        return;
      }

      if (
        myGen !== connectionRequestsFetchGenRef.current ||
        (typeof isAuthGenerationActive === 'function' && !isAuthGenerationActive(authGen))
      ) {
        console.log('STALE_AUTH_RESULT_IGNORED', { reason: 'refreshConnectionRequests' });
        return;
      }

      const userRequests = (incomingRequests || []).map(normalizeIncomingConnectionRequest);
      console.log('✅ V167.INSTANT.FIX: Fetched', userRequests.length, 'requests from database');
      setConnectionRequests(userRequests);
    } catch (err) {
      console.error('❌ V167.INSTANT.FIX: Exception refreshing user requests:', err);
    } finally {
      connectionRequestsInFlightRef.current -= 1;
      if (connectionRequestsInFlightRef.current === 0) {
        // Do not leave loading true across logout — only clear if still same auth gen or no user
        if (!user?.id || typeof isAuthGenerationActive !== 'function' || isAuthGenerationActive(authGen)) {
          setIsRefreshingConnectionRequests(false);
        } else {
          setIsRefreshingConnectionRequests(false);
        }
      }
    }
  }, [user?.id, getAuthGeneration, isAuthGenerationActive]);

  const markOutgoingPending = useCallback((recipientId: string) => {
    if (!recipientId) return;
    setOutgoingPendingIds((prev) => {
      const next = uniqueSortedRecipientIds([...prev, recipientId]);
      if (sameSortedIds(prev, next)) return prev;
      outgoingPendingIdsRef.current = next;
      return next;
    });
  }, []);

  const refreshOutgoingPendingRequests = useCallback(async () => {
    if (!user?.id) return;
    const authGen = typeof getAuthGeneration === 'function' ? getAuthGeneration() : 0;
    const myGen = ++outgoingPendingFetchGenRef.current;
    const snapshot = new Set(outgoingPendingIdsRef.current);
    try {
      const { data, error } = await fetchOutgoingPendingRecipientIds(user.id);
      if (error) {
        console.error('❌ refreshOutgoingPendingRequests failed; retaining last known state:', error);
        return;
      }
      if (
        myGen !== outgoingPendingFetchGenRef.current ||
        (typeof isAuthGenerationActive === 'function' && !isAuthGenerationActive(authGen))
      ) {
        console.log('STALE_AUTH_RESULT_IGNORED', { reason: 'refreshOutgoingPendingRequests' });
        return;
      }
      const markedDuring = outgoingPendingIdsRef.current.filter((id) => !snapshot.has(id));
      const firstDegreeIds = extractFirstDegreeUserIds(getTabConnections ? getTabConnections('first') : []);
      const next = mergeOutgoingPendingIds({
        fetched: data || [],
        markedDuringFetch: markedDuring,
        firstDegreeUserIds: firstDegreeIds,
      });
      if (!sameSortedIds(outgoingPendingIdsRef.current, next)) {
        outgoingPendingIdsRef.current = next;
        setOutgoingPendingIds(next);
      }
    } catch (err) {
      console.error('❌ refreshOutgoingPendingRequests exception; retaining last known state:', err);
    }
  }, [user?.id, getAuthGeneration, isAuthGenerationActive, getTabConnections]);

  const refreshOutgoingPendingRequestsRef = useRef(refreshOutgoingPendingRequests);
  refreshOutgoingPendingRequestsRef.current = refreshOutgoingPendingRequests;
  const prevOutgoingUserIdRef = useRef<string | null>(null);

  // Tear down network async on logout — invalidate gens and clear loading flags immediately
  useEffect(() => {
    const nextId = user?.id ?? null;
    const prev = prevOutgoingUserIdRef.current;
    if (prev === nextId) {
      if (nextId) return;
    } else {
      const isFirstHydration = prev === null && nextId != null;
      prevOutgoingUserIdRef.current = nextId;
      if (!isFirstHydration) {
        outgoingPendingFetchGenRef.current += 1;
        outgoingPendingIdsRef.current = [];
        setOutgoingPendingIds([]);
        if (nextId) {
          void refreshOutgoingPendingRequestsRef.current?.();
        }
      }
    }

    if (user?.id) return;
    connectionRequestsFetchGenRef.current += 1;
    connectionRequestsInFlightRef.current = 0;
    setIsRefreshingConnectionRequests(false);
    setIsRefreshing(false);
    setIsSyncing(false);
    if (hardSyncTimerRef.current) {
      clearTimeout(hardSyncTimerRef.current);
      hardSyncTimerRef.current = null;
    }
    if (rtDebounceRef.current) {
      window.clearTimeout(rtDebounceRef.current);
      rtDebounceRef.current = null;
    }
  }, [user?.id]);

  // V174.8: Refresh network data after mutations (block/disconnect)
  // Plan: opts.reason === 'explicit' sets suppress-realtime so explicit user action wins. Always returns array (never void).
  // V320.RELOAD.3: opts.force passed to loadAllNetworkData for deterministic refetch
  const refreshNetwork = useCallback(async (opts?: { reason?: 'explicit'; force?: boolean }): Promise<NetworkConnection[]> => {
    if (!user?.id || !isReady) {
      console.warn('⚠️ V174.8: Cannot refresh network - no user or auth not ready');
      return [];
    }

    if (opts?.reason === 'explicit') {
      suppressRealtimeUntilRef.current = Date.now() + 1500;
    }

    console.log('🔄 V174.8: refreshNetwork() called - reloading network data (keeping UI stable)');
    setIsRefreshing(true);

    try {
      if (!opts?.force) {
        clearPersistedTabCacheOnly();
        hasInitialized.current = false;
      }
      await loadAllNetworkData(opts?.force ? { force: true } : undefined);

      console.log('✅ V174.8: Network refresh completed');
      return lastFirstDegreeConnectionsRef.current ?? [];
    } catch (error) {
      console.error('❌ V174.8: Failed to refresh network:', error);
      lastFirstDegreeConnectionsRef.current = [];
      return [];
    } finally {
      setIsRefreshing(false);
    }
  }, [user?.id, isReady, clearPersistedTabCacheOnly, loadAllNetworkData]);

  const navigateToNetwork = useCallback(
    (opts: { tab?: string }) => {
      navigate(opts.tab ? `/network?tab=${opts.tab}` : '/network');
    },
    [navigate]
  );

  // V316.FIX: Reload network when user changes visibility in Settings
  useEffect(() => {
    const handleVisibilityChanged = () => {
      if (user?.id && isReady) {
        void loadAllNetworkData({ force: true });
      }
    };
    window.addEventListener('visibility-changed', handleVisibilityChanged);
    return () => window.removeEventListener('visibility-changed', handleVisibilityChanged);
  }, [user?.id, isReady, loadAllNetworkData]);

  /**
   * V320.SYNC: Deterministic Hard Sync - Clears ALL stale caches and forces fresh DB fetch
   * This is called on BOTH clients via real-time events to ensure symmetric UI updates
   */
  const forceHardSync = useCallback(async (reason: string) => {
    if (isSyncing) {
      console.log('[V320.SYNC] Sync already in progress, skipping:', reason);
      return;
    }

    try {
      setIsSyncing(true);
      console.log('[V320.SYNC] Starting hard sync:', reason);

      // Step 1: Clear ALL local caches synchronously
      clearTabCache?.();
      clearProfileStatsCache?.();

      // Clear user cache if it exists as a setter
      setUserCache?.(new Map());

      // Step 2: Parallel re-fetch for speed (use Promise.allSettled to prevent one failure blocking others)
      const results = await Promise.allSettled([
        refreshConnectionRequests?.(),
        refreshNetwork?.({ reason: 'hard-sync' })
      ]);

      // Log any failures for debugging
      results.forEach((result, idx) => {
        if (result.status === 'rejected') {
          console.warn(`[V320.SYNC] Refresh ${idx} failed:`, result.reason);
        }
      });

      // Step 3: Notify external listeners AFTER canonical refetch (Discover/Profile).
      // Pass firstDegreeUserIds only when network refresh fulfilled so consumers
      // do not wipe cache on a failed refetch.
      const networkResult = results[1];
      const detail: {
        reason: string;
        timestamp: number;
        firstDegreeUserIds?: string[];
      } = {
        reason,
        timestamp: Date.now()
      };
      if (networkResult?.status === 'fulfilled') {
        detail.firstDegreeUserIds = extractFirstDegreeUserIds(
          (networkResult.value as NetworkConnection[] | undefined) ?? []
        );
      }

      window.dispatchEvent(
        new CustomEvent(APP_CONNECTION_SYNC, {
          detail
        })
      );

      console.log('[V320.SYNC] Hard sync completed successfully');
    } catch (error) {
      console.error('[V320.SYNC] Hard sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refreshConnectionRequests, refreshNetwork, clearTabCache, clearProfileStatsCache, setUserCache]);

  /**
   * V320.SYNC: Debounced wrapper to handle "Realtime Storms"
   * Collapses multiple rapid events into single sync (e.g., bulk operations)
   */
  const scheduleHardSync = useCallback((reason: string) => {
    if (!user?.id) return;
    if (hardSyncTimerRef.current) {
      clearTimeout(hardSyncTimerRef.current);
    }
    const authGen = typeof getAuthGeneration === 'function' ? getAuthGeneration() : 0;
    hardSyncTimerRef.current = setTimeout(() => {
      if (typeof isAuthGenerationActive === 'function' && !isAuthGenerationActive(authGen)) {
        console.log('STALE_AUTH_RESULT_IGNORED', { reason: 'scheduleHardSync' });
        return;
      }
      if (!user?.id) return;
      void forceHardSync(reason);
    }, 400); // 400ms handles DB consistency lag across Supabase nodes
  }, [forceHardSync, user?.id, getAuthGeneration, isAuthGenerationActive]);

  const { registerNetworkRouter } = useBlissLiveSync();

  useNetworkBlissLiveSyncEffects({
    registerNetworkRouter,
    user,
    isReady,
    scheduleHardSync,
    refreshConnectionRequests,
    setConnectionRequests,
    loadAllNetworkData,
    forceHardSync,
    refreshNetwork,
    setNetworkEpoch,
    navigateToNetwork,
    clearPersistedTabCacheOnly,
    clearProfileStatsCache
  });

  // V163.8.5: Smart update methods to prevent duplicate queries
  const updateCurrentUser = useCallback(async (userData: Partial<CachedUser>) => {
    if (!user?.id) return;
    try {
      console.log('🔄 V163.8.5: Updating current user via NetworkDataContext');
      console.log('📝 V163.8.5: Input userData:', JSON.stringify(userData, null, 2));

      // V213.5: Sanitize userData to prevent blob:/data: URLs from being written to DB
      const { sanitizeUserUpdate } = await import('../utils/sanitizeUserUpdate');
      const sanitizedData = sanitizeUserUpdate(userData);

      console.log('📝 V163.8.5: Sanitized data:', JSON.stringify(sanitizedData, null, 2));
      console.log('📝 V163.8.5: User ID:', user.id);

      // V205.1: Fix 204 handling - check for error, accept data===null as success
      const { error, data: updateResult } = await supabase
        .from('users')
        .update(sanitizedData)
        .eq('id', user.id)
        .select('id, name, email, updated_at')
        .single();

      if (error) {
        console.error('❌ V205.1: Database update error:', error);
        console.error('❌ V205.1: Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('✅ V205.1: Database update successful');
      console.log('✅ V205.1: Updated user data:', JSON.stringify(updateResult, null, 2));

      // Update cache immediately for instant UI updates
      setUserCache(prev => {
        const newCache = new Map(prev);
        const currentUser = newCache.get(user.id);
        if (currentUser) {
          newCache.set(user.id, { ...currentUser, ...userData });
        }
        return newCache;
      });

      // V161.DISCOVER: Clear tab cache when dating_preferences are updated
      // This ensures connections are reloaded with fresh user data
      if (userData.dating_preferences !== undefined) {
        console.log('🔄 V161.DISCOVER: Clearing tab cache to refresh connections with updated dating preferences');
        clearTabCache();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('dating-visibility-changed'));
        }
      }

      // Current user updated in database and cache
    } catch (error) {
      console.error('❌ V163.8.5: Error updating current user:', error);
      throw error;
    }
  }, [user?.id, clearTabCache]);

  const updateCurrentUserProfile = useCallback(async (profileData: any) => {
    if (!user?.id) return;
    try {
      console.log('🔄 V163.8.5: Updating current user profile via NetworkDataContext');

      // P1: Select row so 0-row updates are detectable; insert if profile row missing
      const { data: updatedRow, error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('user_id', user.id)
        .select('id')
        .maybeSingle();

      if (error) {
        console.error('❌ V205.1: Database update error:', error);
        throw error;
      }

      if (!updatedRow && Object.keys(profileData).length > 0) {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({ user_id: user.id, ...profileData });

        if (insertError) {
          console.error('❌ V205.1: Profile insert fallback failed:', insertError);
          throw insertError;
        }
      }

      console.log('✅ V205.1: Database profile write succeeded');

      // Update cache immediately for instant UI updates
      setUserCache(prev => {
        const newCache = new Map(prev);
        const currentUser = newCache.get(user.id);
        if (currentUser) {
          newCache.set(user.id, {
            ...currentUser,
            profiles: { ...currentUser.profiles, ...profileData }
          });
        }
        return newCache;
      });

      console.log('✅ V163.8.5: Current user profile updated in database and cache');
    } catch (error) {
      console.error('❌ V163.8.5: Error updating current user profile:', error);
      throw error;
    }
  }, [user?.id]);

  // V171.UX: Remove connection request from state (for instant UI updates)
  // HOTFIX: Bump fetch generation so in-flight Supabase completions cannot overwrite with stale rows
  const removeConnectionRequest = useCallback((requestId: string) => {
    console.log('🗑️ V171.UX: Removing connection request from context:', requestId);
    connectionRequestsFetchGenRef.current += 1;
    setConnectionRequests(prev => prev.filter(req => req.id !== requestId));
  }, []);

  // Reload network data (e.g. error boundary retry). Wraps refreshNetwork with force.
  const retryBootstrap = useCallback(async (options?: { force?: boolean }) => {
    await refreshNetwork({ force: options?.force ?? true });
  }, [refreshNetwork]);

  const outgoingPendingRecipientIds = useMemo(() => {
    if (outgoingPendingIds.length === 0) return EMPTY_OUTGOING_PENDING_IDS;
    return new Set(outgoingPendingIds) as ReadonlySet<string>;
  }, [outgoingPendingIds]);

  // V157.3 TASK 2: Memoize provider value to prevent unnecessary re-renders
  const providerValue = useMemo(() => ({
    tabCache,
    updateTabCache,
    clearTabCache,
    isTabCacheValid,
    getTabConnections,
    userCache,
    getUser,
    getCurrentUser,
    connectionRequests,
    refreshConnectionRequests,
    outgoingPendingRecipientIds,
    markOutgoingPending,
    refreshOutgoingPendingRequests,
    removeConnectionRequest, // V171.UX: Instant request removal
    updateCurrentUser,
    updateCurrentUserProfile,
    // V167.2ND: 2nd degree connections
    secondDegreeConnections,
    secondDegreeCount: secondDegreeConnections.length,
    // V167.3RD: 3rd degree connections (requires trust score >= 40)
    thirdDegreeConnections,
    thirdDegreeCount: thirdDegreeConnections.length,
    // V174.6: Network refresh for mutations
    refreshNetwork,
    isRefreshing,
    isRefreshingConnectionRequests,
    networkShellVisibleTab,
    setNetworkShellVisibleTab,
    hasUnseenNetworkAlerts,
    markNetworkAlertsSeen,
    // V320.SYNC: Hard sync for deterministic refresh
    forceHardSync,
    isSyncing,
    scheduleHardSync,
    retryBootstrap,
  }), [tabCache, updateTabCache, clearTabCache, isTabCacheValid, getTabConnections, userCache, getUser, getCurrentUser, connectionRequests, refreshConnectionRequests, outgoingPendingRecipientIds, markOutgoingPending, refreshOutgoingPendingRequests, removeConnectionRequest, updateCurrentUser, updateCurrentUserProfile, secondDegreeConnections, thirdDegreeConnections, refreshNetwork, isRefreshing, isRefreshingConnectionRequests, networkShellVisibleTab, hasUnseenNetworkAlerts, markNetworkAlertsSeen, forceHardSync, isSyncing, scheduleHardSync, retryBootstrap]);

  // V157.3 TASK 2: Provider stability monitoring (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 V157.3: NetworkDataContext provider value changed');
    }
  }, [providerValue]);

  return (
    <NetworkDataContext.Provider value={providerValue}>
      {children}
    </NetworkDataContext.Provider>
  );
};


export const useNetworkData = () => {
  const context = useContext(NetworkDataContext);
  if (context === undefined) {
    // V174.8: Return strict NOOP context matching NetworkDataContextType
    if (import.meta.env.DEV) {
      console.warn('⚠️ useNetworkData called outside NetworkDataProvider. Returning no-op context.');
    }
    return NOOP_CONTEXT;
  }
  return context;
};

// V163.2: Convenience hooks for consuming cached users
export function useUser(userId: string | null | undefined): CachedUser | null {
  const { getUser } = useNetworkData();
  if (!userId || !getUser) return null;
  return getUser(userId);
}

export function useCurrentUser(): CachedUser | null {
  const { getCurrentUser } = useNetworkData();
  return getCurrentUser ? getCurrentUser() : null;
}

// V163.8.5: Convenience hooks for smart updates
export function useUpdateCurrentUser() {
  const { updateCurrentUser } = useNetworkData();
  return updateCurrentUser;
}

export function useUpdateCurrentUserProfile() {
  const { updateCurrentUserProfile } = useNetworkData();
  return updateCurrentUserProfile;
}

// V163.3: Expose consolidated pending requests
export function useConnectionRequests(): ConnectionRequest[] {
  const { connectionRequests } = useNetworkData();
  return (connectionRequests || []) as ConnectionRequest[];
}

export function useOutgoingPendingRecipientIds(): ReadonlySet<string> {
  const { outgoingPendingRecipientIds } = useNetworkData();
  return outgoingPendingRecipientIds ?? EMPTY_OUTGOING_PENDING_IDS;
}

// V164.2: Safe Discover hook - filter enriched network connections for dating
// V321: All hooks MUST be called unconditionally (no early returns before hooks) to fix React #311
export function useDiscoverableConnections(): { users: any[], isLoading: boolean } {
  const { getTabConnections, tabCache, getUser, userCache } = useNetworkData();
  // V167.FIX: Use auth user ID directly instead of useCurrentUser() to avoid timing issues
  const { user } = useAuth() as any;
  const currentUserFromCache = useCurrentUser();

  // Resolve currentUser (may be null when deps not ready)
  const currentUser = (user?.id && getUser)
    ? (getUser(user.id) || currentUserFromCache)
    : currentUserFromCache;

  // V321: Defer early-return checks - must call all hooks first for consistent hook order
  const hasRequiredData = !!(getTabConnections && currentUser && tabCache && tabCache.lastUpdated > 0);

  // Safe connection arrays (empty when not ready)
  const allConnections = hasRequiredData ? getTabConnections!('all') : [];

  // Calculate current user age (null when not ready)
  const currentUserAge = hasRequiredData && currentUser?.date_of_birth
    ? new Date().getFullYear() - new Date(currentUser.date_of_birth).getFullYear()
    : null;

  const currentUserDatingPrefs = hasRequiredData && currentUser ? currentUser.dating_preferences : null;

  // V167.FIX: Fetch missing user data BEFORE filtering - hooks called unconditionally
  const missingUserDataMapRef = useRef(new Map<string, any>());
  const [fetchedUserIds, setFetchedUserIds] = useState<Set<string>>(new Set());
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const prevTabCacheUpdatedRef = useRef<number>(0);

  // BlissNet: reset stale peer fetch map when network tab cache reloads after clear
  useEffect(() => {
    const updated = tabCache?.lastUpdated ?? 0;
    if (prevTabCacheUpdatedRef.current === 0 && updated > 0) {
      missingUserDataMapRef.current.clear();
      setFetchedUserIds(new Set());
    }
    prevTabCacheUpdatedRef.current = updated;
  }, [tabCache?.lastUpdated]);

  // Identify connections missing dating_preferences or trust score fields
  const connectionsMissingData = useMemo(() => {
    return allConnections.filter(conn => {
      if (!conn.userId) return false;
      const cachedOtherUser = getUser ? getUser(conn.userId) : null;
      const connAny = conn as NetworkConnection & { otherFriendsCount?: number; otherSecondDegreeCount?: number };
      const hasDatingPrefs = !!(cachedOtherUser?.dating_preferences || conn.otherDatingPreferences);
      const hasTrustScore = Number(cachedOtherUser?.trust_score ?? conn.trustScore ?? 0) > 0;
      const hasFriendsCount =
        cachedOtherUser?.friends_count != null ||
        connAny.otherFriendsCount != null;
      return !hasDatingPrefs || !hasTrustScore || !hasFriendsCount;
    });
  }, [allConnections, getUser]);

  // V174.8: Compute stable key to prevent rerender loops
  const userIdsToFetchKey = useMemo(() => {
    const ids = connectionsMissingData
      .map(c => c.userId)
      .filter(isNonEmptyUuid)
      .sort();
    return ids.join('|');
  }, [connectionsMissingData]);

  // Fetch missing user data in parallel
  useEffect(() => {
    if (connectionsMissingData.length === 0) return;

    const userIdsToFetch = connectionsMissingData
      .map(c => c.userId)
      .filter(isNonEmptyUuid)
      .filter(id => !fetchedUserIds.has(id));

    if (userIdsToFetch.length === 0) return;

    setIsFetching(true);

    const fetchMissingData = async () => {
      try {
        // V180.4.7: Add friends_count to fix Trust Score dialog
        // V334: Parallel fetch users + profiles to get show_age preference
        const [usersResult, profilesResult] = await Promise.all([
          (async () => {
            const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
            return rpcPeerUsersForViewer(supabase, userIdsToFetch, { includePrefs: true });
          })(),
          baseSupabase
            .from('profiles')
            .select('user_id, show_age')
            .in('user_id', userIdsToFetch)
        ]);

        const { data: usersData, error } = usersResult;
        const { data: profilesData } = profilesResult;

        // V334: Build show_age lookup from profiles
        const showAgeMap = new Map<string, boolean>();
        if (profilesData) {
          profilesData.forEach(p => {
            if (p.user_id) showAgeMap.set(p.user_id, p.show_age !== false);
          });
        }

        if (!error && usersData) {
          usersData.forEach(user => {
            missingUserDataMapRef.current.set(user.id, {
              dating_preferences: user.dating_preferences,
              gender: user.gender,
              date_of_birth: user.date_of_birth,
              is_verified: user.is_verified, // V177.VERIFY: Include verification status
              friends_count: user.friends_count, // V180.4.7: Include friends_count for Trust Score
              second_degree_count: user.second_degree_count,
              trust_score: user.trust_score, // V180.4.7: Include trust_score for Trust Score
              show_age: showAgeMap.get(user.id) ?? true // V334: Per-user age visibility (default true)
            });
          });

          // IMPORTANT: update Set based on prev, but don't put the Set itself in deps
          setFetchedUserIds(prev => new Set([...prev, ...userIdsToFetch]));
          console.log(`✅ V180.4.7: Fetched user data including friends_count for ${usersData.length} users`);
        }
      } catch (error) {
        console.warn(`⚠️ V167.DISCOVER: Failed to fetch missing user data:`, error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchMissingData();
  }, [userIdsToFetchKey]); // ✅ only stable key

  // V167.FIX: Fetch declined matches to filter out declined users
  // Also migrate any 'rejected' records to 'declined' for consistency
  const declinedUserIdsRef = useRef<Set<string>>(new Set());
  const [declinedLoaded, setDeclinedLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!currentUser?.id || declinedLoaded) return;

    const fetchDeclinedMatches = async () => {
      try {
        // Step 1: Try to migrate any 'rejected' records to 'declined' for this user
        // Only succeeds if schema constraint allows 'declined'
        const { data: rejectedMatches, error: migrateError } = await supabase
          .from('matches')
          .select('id')
          .or(`user1_id.eq.${currentUser.id},user2_id.eq.${currentUser.id}`)
          .eq('status', 'rejected');

        if (!migrateError && rejectedMatches && rejectedMatches.length > 0) {
          console.log(`🔄 V167.DISCOVER: Attempting to migrate ${rejectedMatches.length} "rejected" records to "declined"`);
          const rejectedIds = rejectedMatches.map(m => m.id);

          // Try to update all rejected matches to declined
          const { error: updateError } = await supabase
            .from('matches')
            .update({ status: 'declined' })
            .in('id', rejectedIds);

          if (updateError) {
            // If constraint violation, schema doesn't support 'declined' yet - that's OK
            if (updateError.code === '23514' && updateError.message?.includes('matches_status_check')) {
              console.log('ℹ️ V167.DISCOVER: Schema doesn\'t support "declined" yet, keeping "rejected" status');
            } else {
              console.warn('⚠️ V167.DISCOVER: Failed to migrate rejected records:', updateError);
            }
          } else {
            console.log(`✅ V167.DISCOVER: Successfully migrated ${rejectedIds.length} records to "declined"`);
          }
        }

        // Step 2: Fetch declined matches
        // Filter for both 'declined' and 'rejected' until migration is complete
        // Once migration runs, all will be 'declined' and we can filter only for 'declined'
        const { data: declinedMatches, error } = await supabase
          .from('matches')
          .select('user1_id, user2_id')
          .or(`user1_id.eq.${currentUser.id},user2_id.eq.${currentUser.id}`)
          .in('status', ['declined', 'rejected']); // Filter for both until migration complete

        if (!error && declinedMatches) {
          const declinedSet = new Set<string>();
          declinedMatches.forEach(match => {
            // Add the other user to declined set
            if (match.user1_id === currentUser.id) {
              declinedSet.add(match.user2_id);
            } else {
              declinedSet.add(match.user1_id);
            }
          });
          declinedUserIdsRef.current = declinedSet;
          setDeclinedLoaded(true);
        }
      } catch (error) {
        console.warn('⚠️ V167.DISCOVER: Failed to fetch declined matches:', error);
        setDeclinedLoaded(true); // Set to true even on error to prevent infinite retries
      }
    };

    fetchDeclinedMatches();
  }, [currentUser?.id, declinedLoaded]);

  // V321: Early return AFTER all hooks (deferred from top) - maintains hook order
  if (!hasRequiredData) {
    if (import.meta.env.DEV) {
      console.log('❌ V167.DISCOVER: Missing dependencies or cache not ready:', {
        hasGetTabConnections: !!getTabConnections,
        hasCurrentUser: !!currentUser,
        hasTabCache: !!tabCache,
        lastUpdated: tabCache?.lastUpdated || 0
      });
    }
    return { users: [], isLoading: true };
  }

  // V167.FIX: Return loading state while fetching missing data or declined matches
  if (isFetching || !declinedLoaded) {
    return { users: [], isLoading: true };
  }

  const gabiDebug = isGabiDiscoverDebug(currentUser?.id);
  const gabiRejectDeclined: Array<{ id: string; name: string; reason: string }> = [];
  const gabiRejectAge: Array<{ id: string; name: string; reason: string }> = [];
  const gabiRejectDating: Array<{ id: string; name: string; reason: string }> = [];
  const gabiRejectIncomplete: Array<{ id: string; name: string; reason: string }> = [];
  const gabiRejectGender: Array<{ id: string; name: string; reason: string }> = [];

  if (gabiDebug) {
    logGabiDiscoverStage(currentUser.id, 'A_currentUser', {
      id: currentUser.id,
      name: currentUser.name,
      dating_preferences: currentUserDatingPrefs,
    });
    logGabiDiscoverStage(currentUser.id, 'B_viewerLookingForDate', {
      value: resolveLookingForDate(currentUserDatingPrefs),
    });
    const rawCandidates = allConnections.filter(
      c => c.id && c.name && c.userId && c.userId !== currentUser.id
    );
    logGabiDiscoverStage(currentUser.id, 'C_rawNetwork', {
      total: allConnections.length,
      first: allConnections.filter(c => c.degree === 1).length,
      second: allConnections.filter(c => c.degree === 2).length,
      third: allConnections.filter(c => c.degree === 3).length,
    });
    logGabiDiscoverStage(currentUser.id, 'D_beforeDatingFilter', {
      count: rawCandidates.length,
    });
  }

  // V164.2: Filter for discoverable matches using enriched connection data
  const discoverableUsers = allConnections
    .filter(conn => {
      // 1. Must have basic data
      if (!conn.id || !conn.name || !conn.userId) {
        return false;
      }

      // 2. Skip current user
      if (conn.userId === currentUser.id) {
        return false;
      }

      // 3. Skip declined users (either direction)
      if (declinedUserIdsRef.current.has(conn.userId)) {
        if (gabiDebug) {
          gabiRejectDeclined.push({ id: conn.userId, name: conn.name, reason: 'declined_match' });
        }
        return false;
      }

      // 🚨 CRITICAL: Do NOT filter out 1st-degree here - let Discover page handle it via toggle
      // 1st-degree connections should be included in the base dataset
      // The Discover page will filter them out at the UI level when toggle is OFF

      // V161.DISCOVER: Enrich connection with fresh user data from cache (fixes stale cache issue)
      // When a user updates their profile, the connection cache may have stale data
      // So we check userCache first, then fall back to connection cache data, then fetched data
      const cachedOtherUser = getUser ? getUser(conn.userId) : null;
      const fetchedData = missingUserDataMapRef.current.get(conn.userId);
      let otherUserDatingPrefs = cachedOtherUser?.dating_preferences || conn.otherDatingPreferences || fetchedData?.dating_preferences;
      const rawOtherGender = cachedOtherUser?.gender || conn.otherGender || fetchedData?.gender;
      let otherUserDateOfBirth = cachedOtherUser?.date_of_birth || conn.otherDateOfBirth || fetchedData?.date_of_birth;

      // V164.2: Age compatibility check using enriched data (use fresh data from cache)
      // V167.MERGE: Use date_of_birth instead of dob
      if (otherUserDateOfBirth) {
        const otherUserAge = new Date().getFullYear() - new Date(otherUserDateOfBirth).getFullYear();

        // Check if current user has age preferences and other user's age fits
        if (currentUserDatingPrefs?.age_preference_min && currentUserDatingPrefs?.age_preference_max) {
          if (otherUserAge < currentUserDatingPrefs.age_preference_min ||
            otherUserAge > currentUserDatingPrefs.age_preference_max) {
            if (gabiDebug) {
              gabiRejectAge.push({
                id: conn.userId,
                name: conn.name,
                reason: `viewer_age_pref:${currentUserDatingPrefs.age_preference_min}-${currentUserDatingPrefs.age_preference_max},candidate_age:${otherUserAge}`,
              });
            }
            return false; // Other user's age doesn't match current user's preference
          }
        }

        // Check if other user has age preferences and current user's age fits (mutual)
        if (currentUserAge && otherUserDatingPrefs?.age_preference_min && otherUserDatingPrefs?.age_preference_max) {
          if (currentUserAge < otherUserDatingPrefs.age_preference_min ||
            currentUserAge > otherUserDatingPrefs.age_preference_max) {
            if (gabiDebug) {
              gabiRejectAge.push({
                id: conn.userId,
                name: conn.name,
                reason: `candidate_age_pref:${otherUserDatingPrefs.age_preference_min}-${otherUserDatingPrefs.age_preference_max},viewer_age:${currentUserAge}`,
              });
            }
            return false; // Current user's age doesn't match other user's preference
          }
        }
      }

      // BlissNet: canonical Discover participation gate (looking_for_date; missing/null -> false, opt-in).
      // Both users must be participating; explicit true opts a user in.
      const currentUserWantsDating = resolveLookingForDate(currentUserDatingPrefs);
      const otherUserWantsDating = resolveLookingForDate(otherUserDatingPrefs);

      if (!currentUserWantsDating || !otherUserWantsDating) {
        console.log(`🎯 BlissNet: Filtered out ${conn.name} - Discover participation OFF:`, {
          currentUser: { name: currentUser.name, wantsDating: currentUserWantsDating },
          otherUser: { name: conn.name, wantsDating: otherUserWantsDating }
        });
        if (gabiDebug) {
          gabiRejectDating.push({
            id: conn.userId,
            name: conn.name,
            reason: `viewer:${currentUserWantsDating},candidate:${otherUserWantsDating}`,
          });
        }
        return false; // At least one user opted out of Discover
      }

      // V164.2: Gender preference compatibility (canonical tokens + legacy keys/synonyms)
      const rawCurrentGender = currentUser.gender;
      const viewerPrefsList = extractGenderPreferenceList(currentUserDatingPrefs);
      const otherPrefsList = extractGenderPreferenceList(otherUserDatingPrefs);
      const gnCurrent = normalizeUserGender(rawCurrentGender);
      const gnOther = normalizeUserGender(rawOtherGender);

      if (!gnCurrent || !gnOther || viewerPrefsList.length === 0 || otherPrefsList.length === 0) {
        console.log(`🎯 V168.DISCOVER: Filtered out ${conn.name} - incomplete dating data:`, {
          currentUser: { name: currentUser.name, gender: rawCurrentGender, genderPrefs: viewerPrefsList },
          otherUser: { name: conn.name, gender: rawOtherGender, genderPrefs: otherPrefsList }
        });
        if (gabiDebug) {
          gabiRejectIncomplete.push({
            id: conn.userId,
            name: conn.name,
            reason: `viewerGender:${gnCurrent ?? 'null'},candidateGender:${gnOther ?? 'null'},viewerPrefs:${viewerPrefsList.length},candidatePrefs:${otherPrefsList.length}`,
          });
        }
        return false;
      }

      const { viewerWantsCandidate, candidateWantsViewer } = mutualGenderDatingCompatible(
        currentUserDatingPrefs,
        otherUserDatingPrefs,
        rawCurrentGender,
        rawOtherGender
      );

      if (!viewerWantsCandidate || !candidateWantsViewer) {
        console.log(`🎯 V168.DISCOVER: Filtered out ${conn.name} - gender preference mismatch:`, {
          currentUser: { name: currentUser.name, gender: gnCurrent, wantsOther: viewerWantsCandidate, prefs: viewerPrefsList },
          otherUser: { name: conn.name, gender: gnOther, wantsCurrent: candidateWantsViewer, prefs: otherPrefsList }
        });
        if (gabiDebug) {
          gabiRejectGender.push({
            id: conn.userId,
            name: conn.name,
            reason: `viewerWants:${viewerWantsCandidate},candidateWants:${candidateWantsViewer}`,
          });
        }
        return false;
      }

      console.log(`✅ V168.DISCOVER: ${conn.name} passed all filters (degree: ${conn.degree})`);
      return true;
    })
    .map(conn => {
      // V164.2: Convert enriched connection to user format for compatibility
      // Get is_verified from cache
      const cachedOtherUser = getUser ? getUser(conn.userId) : null;
      const fetchedData = missingUserDataMapRef.current.get(conn.userId);
      const isVerifiedValue = cachedOtherUser?.is_verified || fetchedData?.is_verified;
      // Handle both boolean and string values from database
      const isVerified = isVerifiedValue === true || isVerifiedValue === 'true' || isVerifiedValue === 1;

      // V180.4.7: Get friends_count and trust_score for Trust Score dialog
      const connAny = conn as NetworkConnection & { otherFriendsCount?: number; otherSecondDegreeCount?: number };
      const friendsCount =
        cachedOtherUser?.friends_count ??
        fetchedData?.friends_count ??
        connAny.otherFriendsCount ??
        0;
      const secondDegreeCount =
        cachedOtherUser?.second_degree_count ??
        fetchedData?.second_degree_count ??
        connAny.otherSecondDegreeCount;
      const trustScore =
        cachedOtherUser?.trust_score ??
        fetchedData?.trust_score ??
        conn.trustScore ??
        0;
      const resolvedSecondDegree =
        secondDegreeCount != null
          ? Number(secondDegreeCount)
          : (() => {
              const ts = Number(trustScore) || 0;
              const fc = Number(friendsCount) || 0;
              if (ts <= 0) return 0;
              const verified = isVerifiedValue === true || isVerifiedValue === 'true' || isVerifiedValue === 1;
              const base = ts - (verified ? 100 : 0);
              return Math.max(0, base - fc * 10);
            })();

      // V334: Enrich date_of_birth from all sources (cache > connection > fetched)
      // Fixes inconsistent age display where conn.otherDateOfBirth could be undefined
      const enrichedDateOfBirth = cachedOtherUser?.date_of_birth || conn.otherDateOfBirth || fetchedData?.date_of_birth;
      const enrichedGender = cachedOtherUser?.gender || conn.otherGender || fetchedData?.gender;
      const enrichedDatingPrefs = cachedOtherUser?.dating_preferences || conn.otherDatingPreferences || fetchedData?.dating_preferences;
      // V334: Get show_age from profiles (via cache or fetched data, default true)
      const enrichedShowAge = cachedOtherUser?.profiles?.show_age ?? fetchedData?.show_age ?? true;

      return {
        id: conn.userId,
        name: conn.name,
        bio: conn.bio,
        avatar: conn.avatar,
        date_of_birth: enrichedDateOfBirth, // V334: Use enriched DOB from cache/connection/fetched
        gender: enrichedGender, // V334: Use enriched gender
        dating_preferences: enrichedDatingPrefs, // V334: Use enriched dating prefs
        show_age: enrichedShowAge !== false, // V334: Per-user age visibility
        is_verified: isVerified, // Include verification status
        friends_count: friendsCount, // V180.4.7: Include friends_count for Trust Score
        second_degree_count: resolvedSecondDegree,
        firstDegreeFriends: friendsCount,
        secondDegreeFriends: resolvedSecondDegree,
        trust_score: Number(trustScore) || 0,
        degree: conn.degree || 1,
        connection_id: conn.id
      };
    });

  if (gabiDebug) {
    summarizeRejects(gabiRejectDeclined, 'I_afterDeclined', currentUser.id);
    summarizeRejects(gabiRejectAge, 'G_afterAge', currentUser.id);
    summarizeRejects(gabiRejectDating, 'E_afterDatingVisibility', currentUser.id);
    summarizeRejects(gabiRejectIncomplete, 'F_afterIncompleteData', currentUser.id);
    summarizeRejects(gabiRejectGender, 'F_afterGender', currentUser.id);
    logGabiDiscoverStage(currentUser.id, 'K_finalDiscoverableUsers', {
      count: discoverableUsers.length,
      ids: discoverableUsers.map(u => ({ id: u.id, name: u.name, degree: u.degree })),
    });
  }

  // 🎯 V168.DISCOVER: Enhanced debugging for degree filtering
  const firstDegreeConns = allConnections.filter(conn => conn.degree === 1);
  const secondDegreeConns = allConnections.filter(conn => conn.degree === 2);
  const thirdDegreeConns = allConnections.filter(conn => conn.degree === 3);
  console.log('🎯 V168.DISCOVER: Filtering results:', {
    totalConnections: allConnections.length,
    firstDegree: firstDegreeConns.length,
    secondDegree: secondDegreeConns.length,
    thirdDegree: thirdDegreeConns.length,
    afterAllFilters: discoverableUsers.length,
    currentUserTrustScore: currentUser.trust_score || 0,
    requiredTrustScoreFor3rd: 40
  });

  // V161.DISCOVER: Enhanced debugging to understand why no profiles appear
  if (discoverableUsers.length === 0) {
    // V161.FIX: Calculate relationship goal for debug logging (was causing ReferenceError)
    const currentUserRelationshipGoal = currentUserDatingPrefs?.relationship_goal?.toLowerCase();
    const currentUserWantsDating = currentUserRelationshipGoal === 'dating' || currentUserRelationshipGoal === 'both';

    console.log('⚠️ Discover: No compatible users found after filtering');
    console.log('🔍 V168.DISCOVER DEBUG:', {
      totalConnections: allConnections.length,
      firstDegree: firstDegreeConns.length,
      secondDegree: secondDegreeConns.length,
      thirdDegree: thirdDegreeConns.length,
      currentUser: {
        id: currentUser.id,
        name: currentUser.name,
        gender: currentUser.gender,
        trustScore: currentUser.trust_score || 0,
        relationshipGoal: currentUserDatingPrefs?.relationship_goal,
        genderPrefs: currentUserDatingPrefs?.gender_preference,
        wantsDating: currentUserWantsDating
      },
      sample2ndOr3rdDegree: (secondDegreeConns[0] || thirdDegreeConns[0]) ? {
        id: (secondDegreeConns[0] || thirdDegreeConns[0]).userId,
        name: (secondDegreeConns[0] || thirdDegreeConns[0]).name,
        degree: (secondDegreeConns[0] || thirdDegreeConns[0]).degree,
        gender: (secondDegreeConns[0] || thirdDegreeConns[0]).otherGender,
        relationshipGoal: (secondDegreeConns[0] || thirdDegreeConns[0]).otherDatingPreferences?.relationship_goal,
        genderPrefs: (secondDegreeConns[0] || thirdDegreeConns[0]).otherDatingPreferences?.gender_preference
      } : null
    });
  } else {
    console.log(`✅ V168.DISCOVER: Found ${discoverableUsers.length} discoverable users (2nd/3rd degree)`);
  }

  return { users: discoverableUsers, isLoading: false };
}
